import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { bookingService } from '@/services/bookingService';
import { Booking, CustomerDetailsResponse, UserRole } from '@shared/types/booking';
import { 
  Calendar, 
  User, 
  MapPin, 
  CreditCard, 
  Phone, 
  Mail, 
  Star,
  Edit,
  X,
  Download,
  Clock
} from 'lucide-react';
import { format } from 'date-fns';

interface BookingDetailsProps {
  bookingId: number;
  userRole: UserRole;
  onEdit?: (booking: Booking) => void;
  onCancel?: (bookingId: number) => void;
}

export function BookingDetails({ bookingId, userRole, onEdit, onCancel }: BookingDetailsProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [customerDetails, setCustomerDetails] = useState<CustomerDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBookingDetails();
  }, [bookingId]);

  const loadBookingDetails = async () => {
    try {
      setLoading(true);
      const [bookingData, customerData] = await Promise.all([
        bookingService.getBooking(bookingId),
        (userRole === 'staff' || userRole === 'admin') 
          ? bookingService.getCustomerDetails(bookingId)
          : Promise.resolve(null)
      ]);
      
      setBooking(bookingData);
      setCustomerDetails(customerData);
    } catch (error) {
      toast({
        title: t('error'),
        description: t('failedToLoadBookingDetails'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!booking) return;
    
    try {
      await bookingService.cancelBooking({
        booking_id: booking.id,
        reason: 'Cancelled by user'
      });
      
      toast({
        title: t('success'),
        description: t('bookingCancelledSuccessfully')
      });
      
      onCancel?.(booking.id);
      loadBookingDetails();
    } catch (error) {
      toast({
        title: t('error'),
        description: t('failedToCancelBooking'),
        variant: 'destructive'
      });
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'confirmed': return 'default';
      case 'canceled': return 'destructive';
      case 'completed': return 'secondary';
      case 'pending': return 'outline';
      default: return 'outline';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Clock className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="text-center p-8">
        <p>{t('bookingNotFound')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Booking Header */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <span>{t('booking')} #{booking.id}</span>
                <Badge variant={getStatusBadgeVariant(booking.status)}>
                  {t(booking.status)}
                </Badge>
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {t('createdOn')} {format(new Date(booking.created_at), 'MMM dd, yyyy HH:mm')}
              </p>
            </div>
            
            <div className="flex space-x-2">
              {(userRole === 'staff' || userRole === 'admin') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit?.(booking)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  {t('edit')}
                </Button>
              )}
              
              {booking.status === 'confirmed' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelBooking}
                >
                  <X className="h-4 w-4 mr-2" />
                  {t('cancel')}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Booking Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Calendar className="h-5 w-5" />
              <span>{t('bookingInformation')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('checkIn')}</p>
                <p className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4" />
                  <span>{format(new Date(booking.check_in), 'MMM dd, yyyy')}</span>
                </p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('checkOut')}</p>
                <p className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4" />
                  <span>{format(new Date(booking.check_out), 'MMM dd, yyyy')}</span>
                </p>
              </div>
            </div>
            
            <Separator />
            
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('property')}</p>
              <p className="flex items-center space-x-2">
                <MapPin className="h-4 w-4" />
                <span>{booking.property_name || `Property #${booking.property_id}`}</span>
              </p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('guestCount')}</p>
              <p className="flex items-center space-x-2">
                <User className="h-4 w-4" />
                <span>{booking.guest_count} {t('guests')}</span>
              </p>
            </div>
            
            {booking.special_requests && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('specialRequests')}</p>
                <p className="text-sm">{booking.special_requests}</p>
              </div>
            )}
            
            {booking.coupon_code && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('couponUsed')}</p>
                <Badge variant="outline">{booking.coupon_code}</Badge>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CreditCard className="h-5 w-5" />
              <span>{t('paymentInformation')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm font-medium text-muted-foreground">{t('totalAmount')}</span>
                <span className="font-semibold">SAR {booking.payment_summary.total_amount}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-sm font-medium text-muted-foreground">{t('depositPaid')}</span>
                <span className="text-green-600">SAR {booking.payment_summary.deposit_paid}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-sm font-medium text-muted-foreground">{t('remainingAmount')}</span>
                <span className="text-orange-600">SAR {booking.payment_summary.remaining_amount}</span>
              </div>
              
              <Separator />
              
              <div className="flex justify-between">
                <span className="text-sm font-medium text-muted-foreground">{t('paymentMethod')}</span>
                <Badge variant="outline">{t(booking.payment_summary.payment_method)}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customer Information (Staff/Admin Only) */}
      {(userRole === 'staff' || userRole === 'admin') && customerDetails && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <User className="h-5 w-5" />
              <span>{t('customerInformation')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('name')}</p>
                  <p className="flex items-center space-x-2">
                    <User className="h-4 w-4" />
                    <span>{customerDetails.customer.name}</span>
                  </p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('email')}</p>
                  <p className="flex items-center space-x-2">
                    <Mail className="h-4 w-4" />
                    <span>{customerDetails.customer.email}</span>
                  </p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('phone')}</p>
                  <p className="flex items-center space-x-2">
                    <Phone className="h-4 w-4" />
                    <span>{customerDetails.customer.phone}</span>
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('language')}</p>
                  <Badge variant="outline">{t(customerDetails.customer.language)}</Badge>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('pastBookings')}</p>
                  <p>{customerDetails.customer.past_bookings} {t('bookings')}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('averageRating')}</p>
                  <p className="flex items-center space-x-2">
                    <Star className="h-4 w-4 text-yellow-500" />
                    <span>{customerDetails.customer.average_rating}/5.0</span>
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
